import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * Shows/hides the message + recipient address fields based on whether the
 * "Personal Message" option is set to its paid value, following the same
 * pattern as the theme's own product-price component: don't recompute the
 * logic in JS, just pull the freshly-rendered piece out of the section HTML
 * Horizon already fetches on variant change, and morph it in.
 */
class PersonalMessageForm extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Instantly show/hide the fields using data already on the event at dispatch
   * time (no network wait) -- this is the perceived-speed fix. The morph in
   * #handleProductSelect below still runs afterwards as the authoritative
   * correction, in case of edge cases (e.g. unavailable combinations) this
   * quick client-side check can't account for.
   * @param {{name: string, value: string}[] | undefined} selectedOptions
   */
  #applyInstantState(selectedOptions) {
    if (!selectedOptions) return;
    const match = selectedOptions.find((o) => o.name === this.dataset.optionName);
    if (!match) return;

    const isOn = match.value === this.dataset.optionValueOn;
    const fieldsWrapper = this.querySelector('[data-personal-message-fields]');
    if (!fieldsWrapper) return;

    fieldsWrapper.hidden = !isOn;
    fieldsWrapper.querySelectorAll('[data-pm-required]').forEach((el) => {
      el.required = isOn;
      if (!isOn) el.value = '';
    });
  }

  /**
   * @param {CustomEvent & {promise?: Promise<any>, target: EventTarget, selectedOptions?: {name: string, value: string}[]}} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#applyInstantState(event.selectedOptions);

    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const newElement = detail.html.querySelector(
          `personal-message-form-component[data-block-id="${this.dataset.blockId}"]`
        );
        if (!newElement) return;

        morph(this, newElement);
      })
      .catch(() => {
        // If the fetch failed or was aborted (e.g. rapid variant switching),
        // leave the fields as they are rather than guessing at new state.
      });
  };
}

if (!customElements.get('personal-message-form-component')) {
  customElements.define('personal-message-form-component', PersonalMessageForm);
}
